import { Button, Card, CardBody, Spinner } from '@nextui-org/react'
import { useEffect, useRef, useState } from 'react'

import { SubTitle, Text } from '@/components/common/UI'
import { Icons } from '@/components/icons'
import { PaymentCard } from '@/components/payment'
import { ModalPayment } from '@/components/payment/ModalPayment'
import { useToast } from '@/context/ToastContext'
import { useAuthHook } from '@/hooks/useAuthHook'
import { usePaymentOrderData } from '@/hooks/usePaymentOrderData'
import { useAspirantsApi } from '@/services/api/aspirantsApi'
import { useInscriptionsApi } from '@/services/api/inscriptionsApi'
import { useResourcesApi } from '@/services/api/resourcesApi'
import { Discount, MessageLevel } from '@/types'
import { ErrorHandlingStrategy } from '@/types/api'
import { AllDataAcademicOfferings } from '@/types/inscription'

interface EnrollmentOrderModuleProps {
    inscriptionId: number // Hacer obligatorio el ID de inscripción
    updateForms?: () => void
}

// eslint-disable-next-line max-lines-per-function, complexity
export const EnrollmentOrderModule = ({
    inscriptionId,
    updateForms
}: EnrollmentOrderModuleProps): JSX.Element => {
    const { paymentDetails, loading, error, fetchPaymentData } =
        usePaymentOrderData()
    const inscriptionsApi = useInscriptionsApi({
        messageLevel: MessageLevel.NONE,
        errorHandling: ErrorHandlingStrategy.THROW
    })
    const { showToast } = useToast()
    const [refreshing, setRefreshing] = useState(false)
    const [showErrorModal, setShowErrorModal] = useState(false)
    const modalShownRef = useRef(false)
    const { idAspirant } = useAuthHook()
    const aspirantsApi = useAspirantsApi({
        messageLevel: MessageLevel.NONE,
        errorHandling: ErrorHandlingStrategy.THROW
    })
    const resourcesApi = useResourcesApi({
        messageLevel: MessageLevel.NONE,
        errorHandling: ErrorHandlingStrategy.THROW
    })
    const [allDataAcademicOfferings, setAllDataAcademicOfferings] =
        useState<AllDataAcademicOfferings | null>(null)
    const [discounts, setDiscounts] = useState<Discount | null>(null)

    // Cargar datos de pago al montar el componente
    useEffect(() => {
        if (inscriptionId) {
            fetchPaymentData(inscriptionId)
        }
    }, [inscriptionId]) // Removemos fetchPaymentData de las dependencias

    // Mostrar modal cuando hay error o cuando el pago está PAGADO
    useEffect(() => {
        if (loading) {
            return
        }
        if (modalShownRef.current) {
            return
        }

        const fetchAcademicOfferingsData = async () => {
            try {
                const { data, status } = await inscriptionsApi.getInscription(
                    inscriptionId.toString()
                )

                if (status !== 200) {
                    return
                }

                const {
                    data: dataAcademicOfferings,
                    status: statusAcademicOfferings
                } = await resourcesApi.getAllDataAcademicOfferings(
                    data.idAcademicOffering
                )

                if (statusAcademicOfferings !== 200) {
                    return
                }

                const { data: dataDiscounts, status: statusDiscounts } =
                    await aspirantsApi.getDiscountsData(inscriptionId)

                if (statusDiscounts === 200) {
                    console.log('dataAcademicOfferings', dataAcademicOfferings)
                    setAllDataAcademicOfferings(dataAcademicOfferings)
                    setDiscounts(dataDiscounts)
                    setShowErrorModal(true)
                    modalShownRef.current = true
                }
            } catch (err) {
                console.error('Error fetching academic offerings data:', err)
            }
        }

        fetchAcademicOfferingsData()
    }, [error, inscriptionId, loading])

    const handlePaymentClick = () => {
        window.open(
            'https://sap-qas.universidadean.edu.co/pagos/index.html?sap-language=ES',
            '_blank'
        )
    }

    const handleRefreshPayment = async () => {
        if (!inscriptionId) {
            showToast({
                message: 'Admisión no encontrada',
                type: 'error'
            })

            return
        }
        setRefreshing(true)
        try {
            const response =
                await inscriptionsApi.updatePaymentOrderStatus(inscriptionId)

            if (response.status === 200) {
                showToast({
                    message: 'Pago actualizado correctamente',
                    type: 'success'
                })
                // Recargar los datos después de actualizar
                await fetchPaymentData(inscriptionId)
                // Recalcular progreso en el backend
                if (idAspirant) {
                    await aspirantsApi.getStatusForms(idAspirant, inscriptionId)
                }
                // Actualizar el porcentaje y estado completado en el contenedor
                updateForms?.()
            } else {
                showToast({
                    message: 'No se encontró orden de pago',
                    type: 'error'
                })
            }
        } catch (error) {
            showToast({
                message: 'Error al actualizar el pago',
                type: 'error'
            })
        } finally {
            setRefreshing(false)
        }
    }

    // Determinar si el pago está completado
    const isPaymentCompleted = paymentDetails?.paymentStatus === 'PAGADO'

    const validateSemesterRegular = (): boolean => {
        if (
            allDataAcademicOfferings?.cycle.includes('SEMESTRE') &&
            allDataAcademicOfferings?.claseOyente.includes('Primera vez') &&
            validateProgram(allDataAcademicOfferings?.program)
        ) {
            return true
        }

        return false
    }

    const validateModuloOrCicle = (cycle: string): boolean => {
        if (cycle.includes('MODULO') || cycle.includes('CICLO')) {
            return true
        }

        return false
    }

    const validateDiscount = (discount: Discount | null): boolean => {
        if (discount?.condiciones === 'ACTIVO') {
            return true
        }

        return false
    }

    const validateConvenio = (studyModality: string): boolean => {
        if (!studyModality.includes('Convenio')) {
            return true
        }

        return false
    }

    const validateProgram = (program: string): boolean => {
        if (program.includes('DOCTORADO')) {
            return false
        }

        return true
    }

    if (loading) {
        return (
            <section className="space-y-6">
                <div className="flex justify-center items-center py-12">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
                    <Text
                        text="Cargando datos de pago..."
                        className="ml-3 text-gray-600"
                    />
                </div>
            </section>
        )
    }

    return (
        <section className="space-y-6">
            {/* Banner de error amigable si no hay datos o hubo fallo */}
            {error && (
                <div className="rounded-2xl bg-gradient-to-r from-orange-50 via-orange-50 to-orange-100 border border-orange-200 shadow-sm px-5 py-4">
                    <div className="flex flex-col gap-3 sm:flex-row sm:items-start">
                        <div className="shrink-0">
                            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-orange-100 text-orange-600 shadow-[0_0_0_1px_rgba(249,115,22,0.15)]">
                                <Icons.InfoCircle className="h-5 w-5" />
                            </div>
                        </div>
                        <div className="w-full">
                            <SubTitle
                                subTitle="Información sobre órdenes de pago"
                                className="text-orange-900 mb-1 text-base sm:text-lg"
                            />
                            <Text
                                text="Aún no se encontraron órdenes de pago para tu admisión. Es posible que estén en proceso de generación."
                                className="text-sm text-orange-900"
                            />
                            <ul className="mt-2 space-y-1.5 text-sm text-orange-900">
                                <li className="flex gap-2">
                                    <span className="mt-1 h-1.5 w-1.5 rounded-full bg-orange-400" />
                                    <span>Valida nuevamente más tarde.</span>
                                </li>
                                <li className="flex gap-2">
                                    <span className="mt-1 h-1.5 w-1.5 rounded-full bg-orange-400" />
                                    <span>
                                        Si conoces tu referencia o factura, puedes continuar con el proceso en la plataforma de pagos.
                                    </span>
                                </li>
                                <li className="flex gap-2">
                                    <span className="mt-1 h-1.5 w-1.5 rounded-full bg-orange-400" />
                                    <span>
                                        Si el pago está pendiente, podrás realizarlo desde el botón.
                                    </span>
                                </li>
                            </ul>
                            <div className="mt-4 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-end">
                                <Button
                                    size="sm"
                                    color="success"
                                    variant="solid"
                                    className="font-semibold w-full sm:w-auto"
                                    startContent={
                                        <Icons.Economy className="w-4 h-4" />
                                    }
                                    onClick={handlePaymentClick}
                                >
                                    Ir a la plataforma de pagos
                                </Button>
                                <Button
                                    size="sm"
                                    variant="bordered"
                                    color="default"
                                    className="font-semibold w-full sm:w-auto border-black text-black hover:bg-black/5"
                                    onPress={handleRefreshPayment}
                                    isDisabled={refreshing || loading}
                                    startContent={
                                        refreshing ? (
                                            <Spinner size="sm" />
                                        ) : (
                                            <Icons.Process className="w-4 h-4" />
                                        )
                                    }
                                >
                                    {refreshing ? 'Actualizando...' : 'Actualizar estado'}
                                </Button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {error &&
                allDataAcademicOfferings &&
                (validateModuloOrCicle(allDataAcademicOfferings.cycle) ||
                    validateDiscount(discounts)) &&
                validateConvenio(allDataAcademicOfferings.studyModality) && (
                    <ModalPayment
                        isOpen={showErrorModal}
                        onClose={() => {
                            setShowErrorModal(false)
                            modalShownRef.current = false
                        }}
                        text="enrollmentOrder.errorModal"
                        allDataAcademicOfferings={allDataAcademicOfferings}
                        discount={discounts}
                        error={true}
                    />
                )}

            {/* Mostrar la PaymentCard cuando hay datos de pago */}
            {paymentDetails && (
                <div className="mb-6">
                    <div className="flex items-center justify-end mb-2">
                        <Button
                            size="sm"
                            variant="flat"
                            color="primary"
                            onPress={handleRefreshPayment}
                            isDisabled={refreshing || loading}
                            startContent={
                                refreshing ? (
                                    <Spinner size="sm" />
                                ) : (
                                    <Icons.Process className="w-4 h-4" />
                                )
                            }
                        >
                            {refreshing ? 'Actualizando...' : 'Actualizar pago'}
                        </Button>
                    </div>
                    <PaymentCard
                        paymentDetails={paymentDetails}
                        className="mb-6"
                    />
                </div>
            )}

            {/* Mostrar sección de pago cuando el pago NO está completado y no hay advertencias */}
            {!isPaymentCompleted && !error && (
                <Card className="shadow-sm border">
                    <CardBody className="p-4">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-3">
                                <Icons.Economy className="w-8 h-8 text-green-600" />
                                <div>
                                    <SubTitle
                                        subTitle="Pago de Matrícula"
                                        className="text-lg font-semibold mb-1"
                                    />
                                    <Text
                                        text={
                                            isPaymentCompleted
                                                ? 'Pago realizado'
                                                : 'Completa el proceso de admisión'
                                        }
                                        className="text-sm text-gray-600"
                                    />
                                </div>
                            </div>
                            <Button
                                color="success"
                                size="md"
                                className="font-semibold"
                                startContent={
                                    <Icons.Economy className="w-4 h-4" />
                                }
                                onClick={handlePaymentClick}
                            >
                                {isPaymentCompleted
                                    ? 'Ver plataforma de pagos'
                                    : 'Pagar Matrícula'}
                            </Button>
                        </div>
                    </CardBody>
                </Card>
            )}

            {isPaymentCompleted && validateSemesterRegular() && (
                <ModalPayment
                    isOpen={showErrorModal}
                    onClose={() => {
                        setShowErrorModal(false)
                        modalShownRef.current = false
                    }}
                    text="enrollmentOrder.admisionModal"
                    allDataAcademicOfferings={allDataAcademicOfferings}
                    discount={discounts}
                    error={false}
                />
            )}
        </section>
    )
}
